import {createSelector} from 'reselect';
import attributes from '../../tracking/attributes';
import {getSelectedDeliveryData} from '../DeliveryAddressSelectors/DeliveryAddressSelectors';
import {
  doDeliveryOptionsIncludeTaxableOption,
  doDeloptsIncludeResidentIDRequiredOption,
  getSelectedOptionOrSubOption,
} from '../DeliveryOptionSelectorsUtils';
import {getSelectedDeliveryCountry, getSettings, getSiteDataV2} from '../selectors';

export const getDeliveryGroups = state => state.deliveryOption?.deliveryGroups;
export const getDeliveryOptionsMessaging = state => state.deliveryOption?.deliveryOptionsMessaging;

export const getDeliveryGroupSelections = createSelector(
  [getDeliveryGroups],
  deliveryGroups => deliveryGroups
    ?.map(deliveryGroup => ({
      id: deliveryGroup.id,
      deliveryOption: deliveryGroup.deliveryOptions[deliveryGroup.selectedOptionIndex],
      basketItems: deliveryGroup.basketItems,
      isFastTrackGroup: deliveryGroup.deliveryOptions.some(
        option => option.fastDelivery,
      ),
      fastTrackItem: deliveryGroup.basketItems?.filter(item => item.fastDelivery),
      optional: deliveryGroup.optional,
      used: !!(deliveryGroup.used),
    })),
);

export const getSelectedDeliveryOptionList = createSelector(
  [getDeliveryGroupSelections],
  deliveryGroupSelections => deliveryGroupSelections?.map(selection => selection.deliveryOption),
);

export const getSelectedDeliveryOptionIDList = createSelector(
  [getSelectedDeliveryOptionList],
  selectedDeliveryOptionList => selectedDeliveryOptionList?.map(deliveryOption => deliveryOption?.id),
);

export const isOptionalGroupUsed = createSelector(
  [getDeliveryGroups],
  deliveryGroups => !!deliveryGroups
    && deliveryGroups.some(deliveryGroup => deliveryGroup.optional && deliveryGroup.used),
);

export const getNewSelectedDeliveryOptionRequestData = createSelector(
  [getDeliveryGroupSelections, getSelectedDeliveryData],
  (deliveryGroupSelections, deliveryOptionsRequest) => ({
    groupSelections: deliveryGroupSelections
      .filter(deliveryGroup => deliveryGroup.deliveryOption)
      .map(deliveryGroup => ({
        groupId: deliveryGroup.id,
        deliveryOptionType: deliveryGroup.deliveryOption.id || deliveryGroup.deliveryOption.optionType,
        subDeliveryOptionIndex: deliveryGroup.deliveryOption.selectedDeliverableDateIndex,
        used: !deliveryGroup.optional || deliveryGroup.used,
      })),
    deliveryOptionsRequest,
  }),
);

export const isATaxableDeliveryOptionSelected = createSelector(
  [getSelectedDeliveryOptionList],
  doDeliveryOptionsIncludeTaxableOption,
);

export const requiresResidentIdOptionSelected = createSelector(
  [getSelectedDeliveryOptionList],
  doDeloptsIncludeResidentIDRequiredOption,
);

export const isAnyDeliveryOptionTaxable = createSelector(
  [getDeliveryGroups],
  deliveryGroups => deliveryGroups.map(deliveryGroup => deliveryGroup.deliveryOptions)
    .some(doDeliveryOptionsIncludeTaxableOption),
);

export const doMultipleGroupsRequireResidentId = createSelector(
  [getDeliveryGroups],
  deliveryGroups => deliveryGroups.map(deliveryGroup => deliveryGroup.deliveryOptions)
    .filter(doDeloptsIncludeResidentIDRequiredOption).length > 1,
);

export const isShippingToChinaWithTaxEnabled = createSelector(
  [getSelectedDeliveryCountry, getSettings],
  (selectedDeliveryCountry, settings) => (
    selectedDeliveryCountry === 'CN' && !!(settings.delivery_countries_to_show_tax?.includes('CN'))
  ),
);

export const isShippingToKorea = createSelector(
  [getSelectedDeliveryCountry],
  selectedDeliveryCountry => selectedDeliveryCountry === 'KR',
);

export const hasContactNumberHelpLabel = createSelector(
  [getSiteDataV2],
  (siteDataV2) => {
    const localesWithMobilePlaceHolders = ['kr'];
    return !localesWithMobilePlaceHolders.includes(siteDataV2.countryCode);
  },
);

export const getOptimalDeliveryOption = (state) => {
  const deliveryGroups = getDeliveryGroups(state);
  
  if (!deliveryGroups.length) return;
  
  const optimalDeliveryOption = {
    price: 0,
    selectedOptionIndex: 0,
    groupId: 0,
  };
  
  deliveryGroups.filter(group => !group.optional).forEach((group) => {
    const selectedDeliveryOption = getSelectedOptionOrSubOption(group.deliveryOptions[group.selectedOptionIndex]);
    if (!selectedDeliveryOption || !selectedDeliveryOption.price) return;
    const price = selectedDeliveryOption.price.amount || selectedDeliveryOption.price.value;
    if (price > optimalDeliveryOption.price) {
      optimalDeliveryOption.price = price;
      optimalDeliveryOption.selectedOptionIndex = group.selectedOptionIndex;
      optimalDeliveryOption.groupId = group.id;
    }
  });
  
  return optimalDeliveryOption;
};

export const getMaxPriceGroupIndex = (state) => {
  const deliveryGroups = getDeliveryGroups(state);
  let maxPrice = 0;
  let index = 0;
  
  // Todo this may need changing
  deliveryGroups.map((group) => {
    const deliveryOptionMaxPrice = Math.max.apply(Math, group.deliveryOptions.map(option => option.price.amount));
    if (deliveryOptionMaxPrice > maxPrice) {
      maxPrice = deliveryOptionMaxPrice;
      index = group.id;
    }
  });
  return index;
};

export const hasOptionalGroup = createSelector(
  [getDeliveryGroups],
  deliveryGroups => !!deliveryGroups
    && deliveryGroups.some(deliveryGroup => deliveryGroup.optional),
);

export const hasFastTrackDeliveryOption = createSelector(
  [getDeliveryGroups],
  (deliveryGroups) => {
    const groupHasFastDelivery = deliveryGroup => deliveryGroup.deliveryOptions.some(
      deliveryOption => deliveryOption.fastDelivery,
    );
    return (
      deliveryGroups.some(groupHasFastDelivery)
    );
  },
);

export const getAllBasketItems = createSelector(
  [getDeliveryGroups],
  deliveryGroups => deliveryGroups.filter(deliveryGroup => !deliveryGroup.optional).reduce(
    (allBasketItems, deliveryGroup) => [...allBasketItems, ...deliveryGroup.basketItems], [],
  ),
);

// Todo - update to include sub selected options
export const getDeliveryOptionsTrackingAttributes = createSelector(
  [getDeliveryGroupSelections],
  deliveryGroupSelections => deliveryGroupSelections.map(
    deliveryGroup => attributes.getAttribute(
      `selectedDeliveryOption.group${deliveryGroup.id}`,
      deliveryGroup.deliveryOption ? (deliveryGroup.deliveryOption.optionType || deliveryGroup.deliveryOption.id) : -1,
    ),
  ),
);

export const hasDeliveryOptions = createSelector(
  [getDeliveryGroups],
  deliveryGroups => !!deliveryGroups
    && deliveryGroups.length > 0
    && deliveryGroups.every(
      deliveryGroup => deliveryGroup.deliveryOptions && deliveryGroup.deliveryOptions.length > 0,
    ),
);

export const isFastDeliverySelected = createSelector(
  [hasOptionalGroup, isOptionalGroupUsed, hasFastTrackDeliveryOption],
  (optionalGroup, optionalGroupUsed, fastTrackDeliveryOption) => (optionalGroup
    ? optionalGroupUsed : fastTrackDeliveryOption),
);
